import React, { useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { EventsState } from '../../../../../../store/types';
import {
	ColorKeys,
	CreateDocument,
	Document,
	PageModule,
	TextOverrides,
	ThumbnailStatus
} from '../../../../../../types/working-model';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import TextInput, { Validation } from '../../../../../general-ui/text-input/text';
import ModalComponent from '../../../../../general-ui/modal/modal';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import { useParams } from 'react-router';
import LargeButton from '../../../../../general-ui/button/large-button';
import {
	addDocument,
	loadDocuments,
	updateDocument,
} from '../../../../../../store/actions/admin/documents';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import {
	UploadAndConvertDocument,
	UploadFile,
} from '../../../../../../connection/uploads';
import Checkbox from '../../../../../general-ui/checkbox/checkbox';
import {
	updateDocumentName,
	updateDocumentThumbnail,
} from '../../../../../../connection/content-connection';
import { GetDefaultDocument } from '../../../../../../store/utils/create-event';
import { bytesToString, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Controls from '../../session-module-controls/controls';
import DocumentCard from '../../../../../live-event/session/session-modules/documents/document-card';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import useRequiredFields from '../../../../../../utils/use-required-fields';
import { ParamsProps } from '../../../../../live-event/live-event';
import { getStylingOverrides } from '../../../../../live-event/utils';

import './documents.scss';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import FileInput from '../../../../../../utils/file-input';
import { allowedFileTypes } from '../../../../../../store/utils/content-utils';

interface Props {
	page_module: PageModule;
	template: string;
}

export default function Documents(props: Props): JSX.Element | null {
	const { page_module, template } = props;
	const {
		CreateSessionReducer: { workingSession },
		AuthReducer: { token, user },
		DocumentsReducer: { documents },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const { language } = useParams() as ParamsProps;
	const thumbnailInput = useRef<HTMLInputElement | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [workingDocument, setWorkingDocument] = useState<
		Document | CreateDocument | null
	>(null);
	const [documentToRemove, setDocumentToRemove] = useState<Document | null>(
		null
	);
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
	const [newThumbnailUrl, setNewThumbnailUrl] = useState('');
	const [newDocumentName, setNewDocumentName] = useState('');

	const [uploading, setUploading] = useState(false);
	const [uploadingImage, setUploadingImage] = useState(false);

	const { backgroundColor, backgroundImg } = getStylingOverrides(page_module.styling_overrides);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const {
		requiredFields,
		resetRequiredFields,
		updateRequiredField,
	} = useRequiredFields(['thumbnail']);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...page_module.content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						}),
					},
					is_edited: true,
				})
			);
		};
	}
	function handleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...page_module.styling_overrides,
				content: {
					...page_module.styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	function handleDragEnd(event: any) {
		if (!page_module.modules) return;
		const _modules = [...page_module.modules];

		const startIndex = _modules.findIndex(
			(document: Document) =>
				Number(document.document) === Number(event.active.id)
		);
		const newIndex = _modules.findIndex(
			(document: Document) =>
				Number(document.document) === Number(event.over.id)
		);

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updatePageModule({
				...page_module,
				modules: _modules,
				content_modules: _modules.map(
					(document: Document) => document.document
				),
				is_edited: true,
			})
		);
	}

	function handleSearch({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(value);
	}

	function editDocument(document: Document) {
		return () => {
			setWorkingDocument(document);
			setModalOpen(true);
		};
	}

	function checkDeleteDocument(document: Document) {
		return () => {
			setDocumentToRemove(document);
			setConfirmingDelete(true);
		};
	}

	function openModal() {
		setModalOpen(true);
	}

	function closeModal() {
		setModalOpen(false);
		// delay this state change until modal is closed
		// otherwise modal display will flash old state
		setTimeout(() => setWorkingDocument(null), 500);
	}

	function cancel() {
		if (workingDocument) {
			resetRequiredFields();
			setWorkingDocument(null);
		}
		else { closeModal(); }
	}

	async function finishedAddingDocument() {
		if (!token) return;
		if (!workingDocument) {
			dispatch(
				updatePageModule({
					...page_module,
					modules: documents.filter((document: Document) =>
						selectedDocuments.includes(document.document)
					),
					content_modules: selectedDocuments,
					is_edited: true,
				})
			);

			setModalOpen(false);
		} else {
			const _document = { ...workingDocument };

			if ('document' in _document) {
				let docUpdate = { ..._document };

				if (newThumbnailUrl) {
					await updateDocumentThumbnail(
						newThumbnailUrl,
						_document.document,
						token
					);
					docUpdate.thumbnail = newThumbnailUrl;
					docUpdate.thumbnail_status = ThumbnailStatus.done;
				}

				if (newDocumentName) {
					docUpdate = await updateDocumentName(
						newDocumentName,
						_document.document,
						token
					);
				}

				dispatch(updateDocument(docUpdate));
				setNewThumbnailUrl('');
				setNewDocumentName('');
				setWorkingDocument(null);
			}
		}
	}

	function setDocumentDisplayName({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setNewDocumentName(value);
	}

	function createNewDocument() {
		setWorkingDocument(GetDefaultDocument());
	}

	async function handleFile(file: File | FileList) {
		if (!user || !token) return;
		const _file = file as File;
		setUploading(true);

		const newDoc = await UploadAndConvertDocument(user, token, _file);
		updateRequiredField('thumbnail', newDoc.thumbnail);
		setWorkingDocument(newDoc);
		dispatch(addDocument(newDoc));
		setUploading(false);
	}

	function updateImage() {
		thumbnailInput.current?.click();
	}

	async function handleThumbnailUpdate({
		target,
	}: React.ChangeEvent<HTMLInputElement>) {
		if (!token || !user) return;
		if (target.files?.[0]) {
			setUploadingImage(true);
			const file = await UploadFile(user, token, target.files[0]);
			setNewThumbnailUrl(file);
			setUploadingImage(false);
		}
	}

	function closeDeleteModal() {
		setConfirmingDelete(false);
	}

	function removeDocument() {
		if (!page_module.modules || !documentToRemove || !page_module.content_modules) return;
		dispatch(
			updatePageModule({
				...page_module,
				modules: page_module.modules.filter(
					(document: Document) =>
						document.document !== documentToRemove.document
				),
				content_modules: page_module.content_modules.filter(
					(module: number) => module !== documentToRemove.document
				),
				is_edited: true,
			})
		);

		setDocumentToRemove(null);
		setConfirmingDelete(false);
	}

	function toggleDocument(document: Document, isOn: boolean) {
		let selectedModules = [...selectedDocuments];

		if (isOn) {
			selectedModules = [...selectedModules, document.document];
		} else {
			selectedModules = selectedModules.filter(
				(module: number) => module !== document.document
			);
		}

		setSelectedDocuments(selectedModules);
	}

	useEffect(() => {
		if (!user || !token) return;
		dispatch(loadDocuments(user.active_channel, token));
	}, [dispatch, token, user]);

	const modalTitle = useMemo(() => {
		if (workingDocument) return 'Upload Document';
		return 'Edit Documents';
	}, [workingDocument]);

	const visibleDocuments = useMemo(() => {
		const term = searchTerm
			.toLowerCase()
			.trim()
			.replace(/ /g, '');

		if (!term) { return documents; }

		return documents.filter((document: Document) => {
			const shouldUseLanguage = !!document.display_name[language];
			const name = (document.display_name[shouldUseLanguage ? 'language' : 'base'] as string)
				.toLowerCase()
				.trim()
				.replace(/ /g, '');
			const filename = document.original_name
				.toLowerCase()
				.trim()
				.replace(/ /g, '');

			return filename.includes(term) || name.includes(term);
		});
	}, [documents, searchTerm, language]);

	const { custom_heading } = getTextValue(page_module.content, language);

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div className={classNames(
			"editor-wrapper documents",
			backgroundColor,
			template
		)}
			style={backgroundImg}
		>
			<Controls
				page_module={page_module}
				openEditModal={openModal}
			/>
			<div className="container">
				<EditableTextSlim
					className="documents-custom_heading"
					tagType="h5"
					value={t(`${namespace}:documents.${page_module.id}.heading`, t(`${namespace}:documents.heading`, custom_heading))}
					onChange={handleTextChange("custom_heading")}
					defaultColor={ColorKeys.bodyTextColor}
					colorOverride={page_module.styling_overrides?.content?.custom_heading?.color}
					onOverrideChange={handleOverrideChange("custom_heading")}
				/>

				<div className="documents-content">
					{page_module.modules && page_module.modules.length ? (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={page_module.modules.map(
									(module: Document) =>
										String(module.document)
								)}
								strategy={rectSortingStrategy}
							>
								<div className="documents-list">
									{page_module.modules.map(
										(document: Document, index: number) => (
											<DocumentComponent
												template={template}
												document={document}
												key={index}
												id={String(document.document)}
												editDocument={editDocument}
												checkDeleteDocument={
													checkDeleteDocument
												}
											/>
										)
									)}

								</div>
							</SortableContext>
						</DndContext>
					) : (
						<LargeButton
							title="Add Documents"
							onClick={() => setModalOpen(true)}
							nonUpload
						/>
					)}
				</div>
			</div>
			<ModalComponent
				size="normal"
				subtitle="Select documents to add to the page, or upload new ones."
				closeable={false}
				cancellable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={modalTitle}
				footer={
					<>
						<button onClick={cancel} style={{ marginRight: 16 }}>
							Cancel
						</button>
						<button
							onClick={finishedAddingDocument}
							className="primary"
						>
							Done
						</button>
					</>
				}
			>
				{workingDocument ? (
					<div>
						<TextInput
							placeholder={'Enter Document Name'}
							label={'Document Name'}
							onChange={setDocumentDisplayName}
							defaultValue={
								(workingDocument.display_name[
									language
								] as string) ??
								workingDocument.display_name[language]
							}
						/>

						<div className="document-image">
							{'document' in workingDocument ? (
								<div className="document-image-data">
									<div className="image-container">
										<img
											src={
												newThumbnailUrl
													? newThumbnailUrl
													: (workingDocument.thumbnail ?? undefined)
											}
											alt={workingDocument.thumbnail ?? ''}
										/>
									</div>
									<div className="image-actions">
										<p>
											Original Name:{' '}
											<strong>
												{workingDocument.original_name}
											</strong>
										</p>
										<p>
											Filesize:{' '}
											<strong>
												{bytesToString(
													workingDocument.filesize
												)}
											</strong>
										</p>
										<button
											onClick={updateImage}
											className="small cyan"
										>
											Change Image
										</button>
										<FileInput
											onChange={handleThumbnailUpdate}
											accept={["image/jpeg", "image/png", "image/gif"]}
											type="file"
											style={{ display: 'none' }}
											ref={thumbnailInput}
										/>
										{uploadingImage && (
											<div className="uploading-image">
												<WaitingIndicator />
											</div>
										)}
									</div>
								</div>
							) : (
								<>
									<div className={classNames("document-image", { error: requiredFields.thumbnail === Validation.error })}>
										{uploading ? (
											<WaitingIndicator />
										) : (
											<LargeButton
												title="Upload Documents"
												subtitle={'Suggested file types are .pdf, .doc, .ppt and up to 50 MB file size'}
												onFile={handleFile}
												style={{
													marginBottom: 32,
													borderRadius: 16,
												}}
												allowedFileTypes={allowedFileTypes.Documents}
											/>
										)}
									</div>
								</>
							)}
						</div>
					</div>
				) : (
					<div className="select-documents">
						<div className="modal-search">
							<TextInput
								onChange={handleSearch}
								placeholder={'Search Documents...'}
								icon={ICONS.SEARCH}
								size={'small'}
							/>
							<p>or</p>
							<button
								className="small"
								onClick={createNewDocument}
							>
								Upload
							</button>
						</div>

						<div className="documents-list">
							{visibleDocuments.map((document: Document) => (
								<AddDocument
									selected={selectedDocuments}
									onClick={toggleDocument}
									document={document}
									editDocument={editDocument}
									key={document.document}
								/>
							))}
						</div>
					</div>
				)}
			</ModalComponent>
			<ModalComponent
				open={confirmingDelete}
				title="Remove Document?"
				onRequestClose={closeDeleteModal}
				cancellable
				closeable={false}
			>
				<>
					<p>
						Are you sure you want to remove{' '}
						<strong>
							{documentToRemove?.display_name[language] ??
								documentToRemove?.display_name.base}
						</strong>
						?
					</p>

					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 32,
						}}
					>
						<button onClick={closeDeleteModal} style={{ marginRight: 16 }}>Cancel</button>
						<button className="primary" onClick={removeDocument}>Remove</button>
					</div>
				</>
			</ModalComponent>
		</div>
	) : null;
}

interface AddProps {
	document: Document;
	onClick: (document: Document, isOn: boolean) => void;
	selected: any[];
	editDocument: (document: Document) => () => void;
}

function AddDocument(props: AddProps) {
	const { document, selected, onClick, editDocument } = props;

	const { language } = useParams() as any;

	function handleChange(e: any, isOn: boolean) {
		onClick(document, isOn);
	}

	const isSelected = useMemo(() => {
		return selected.includes(document.document);
	}, [document.document, selected]);

	function handleEdit(e: MouseEvent) {
		e.stopPropagation();
		editDocument(document)();
	}

	return (
		<div className="add-document">
			<div
				className={classNames("image-container", { selected: isSelected })}
				style={
					document.thumbnail
						? {
							backgroundImage: `url(${document.thumbnail.replace(
								/\s/g,
								'%20'
							)})`,
						}
						: {}
				}
				// toggle the selected document
				onClick={() => handleChange('', !isSelected)}
			>
				<div className="document-controls">
					<Checkbox
						activeColor="cyan"
						checked={isSelected}
						onChange={handleChange}
						value={
							(document.display_name[language] as string) ??
							document.display_name.base
						}
						size={20}
						color={isSelected ? COLORS.CYAN : COLORS.WHITE}
						backgroundColor={isSelected ? COLORS.WHITE : undefined}
						selectedIconName={ICONS.CHECKBOX_BORDERLESS}
					/>
					<div className="actions">
						<button onClick={handleEdit} className="no-style">
							<Icon
								name={ICONS.EDIT}
								size={20}
								color={COLORS.WHITE}
							/>
						</button>
					</div>
				</div>
			</div>
			<h6 className="display-name">
				{(document.display_name[language] as string) ??
					document.display_name.base}
			</h6>
		</div>
	);
}

interface DocProps {
	id: string;
	document: Document;
	editDocument: (document: Document) => () => void;
	checkDeleteDocument: (document: Document) => () => void;
	template: string;
}

function DocumentComponent(props: DocProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id: props.id });

	const { document, editDocument, checkDeleteDocument, template } = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="document-wrapper"
		>
			<DocumentCard
				document={document}
				template={template}
			/>

			<div className="edit-actions">
				<button className="round" onClick={editDocument(document)}>
					<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />
				</button>
				<button
					className="round"
					onClick={checkDeleteDocument(document)}
				>
					<Icon name={ICONS.TRASH} size={12} color={COLORS.WHITE} />
				</button>
			</div>
		</div>
	);
}
